import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';
import { getUsers, createUser, updateUser, deleteUser, getClients } from '../api';
import { User, UserRole } from '../types/user';
import { Client } from '../types/client';
import { useNotification } from '../contexts/NotificationContext';
import { formatDate } from '../utils';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: UserRole.Warehouse,
    clientId: undefined as number | undefined,
  });
  const [formErrors, setFormErrors] = useState({
    password: '',
  });
  const { addNotification } = useNotification();
  const dt = useRef<DataTable<User[]>>(null);

  useEffect(() => {
    fetchUsers();
    fetchClients();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      addNotification('error', 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const data = await getClients();
      setClients(data);
    } catch (error) {
      addNotification('error', 'Failed to fetch clients');
    }
  };

  const handleOpenDialog = (user: User | null = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        role: user.role,
        clientId: user.clientId,
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        role: UserRole.Warehouse,
        clientId: undefined,
      });
    }
    setFormErrors({ password: '' });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      role: UserRole.Warehouse,
      clientId: undefined,
    });
    setFormErrors({ password: '' });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear password error when user starts typing
    if (name === 'password') {
      setFormErrors(prev => ({ ...prev, password: '' }));
    }
  };

  const handleRoleChange = (e: DropdownChangeEvent) => {
    const newRole = e.value;
    setFormData(prev => ({
      ...prev,
      role: newRole,
      // Clear clientId if the new role is not Customer
      clientId: newRole === UserRole.Customer ? prev.clientId : undefined
    }));
  };

  const handleClientChange = (e: DropdownChangeEvent) => {
    setFormData(prev => ({ ...prev, clientId: e.value }));
  };

  const validateForm = () => {
    const errors = { password: '' };
    let isValid = true;

    // Only validate password if it's provided for editing or if creating new user
    if (formData.password || !editingUser) {
      if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters long';
        isValid = false;
      }
    }

    // Validate client selection for Customer role
    if (formData.role === UserRole.Customer && !formData.clientId) {
      addNotification('error', 'Client must be selected for Customer role');
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (editingUser) {
        // Only include password in update if it's been changed
        const updateData = {
          ...formData,
          password: formData.password || undefined
        };
        const updatedUser = await updateUser(editingUser.id, updateData);
        setUsers(prevUsers => prevUsers.map((user) => (user.id === updatedUser.id ? updatedUser : user)));
        addNotification('success', 'User updated successfully');
      } else {
        const newUser = await createUser(formData);
        setUsers(prevUsers => [...prevUsers, newUser]);
        addNotification('success', 'User created successfully');
      }
      handleCloseDialog();
    } catch (error: any) {
      if (error.response?.data?.message) {
        addNotification('error', error.response.data.message);
      } else {
        addNotification('error', `Failed to ${editingUser ? 'update' : 'create'} user`);
      }
    }
  };

  const handleDeleteUser = async (id: number) => {
    confirmDialog({
      message: 'Are you sure you want to delete this user?',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await deleteUser(id);
          setUsers(prevUsers => prevUsers.filter((user) => user.id !== id));
          addNotification('success', 'User deleted successfully');
        } catch (error) {
          addNotification('error', 'Failed to delete user');
        }
      },
    });
  };

  const actionBodyTemplate = (rowData: User) => {
    return (
      <div className="flex justify-content-center">
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-success mr-2" onClick={() => handleOpenDialog(rowData)} />
        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => handleDeleteUser(rowData.id)} />
      </div>
    );
  };

  const clientBodyTemplate = (rowData: User) => {
    if (rowData.role !== UserRole.Customer) return '';
    return rowData.client?.name || '';
  };

  const roleOptions = [
    { label: 'Admin', value: UserRole.Admin },
    { label: 'Warehouse', value: UserRole.Warehouse },
    { label: 'Customer', value: UserRole.Customer },
  ];

  const clientOptions = clients.map(client => ({
    label: client.name,
    value: client.id
  }));

  const dialogFooter = (
    <>
      <Button label="Cancel" icon="pi pi-times" onClick={handleCloseDialog} className="p-button-text" />
      <Button label={editingUser ? 'Update' : 'Create'} icon="pi pi-check" onClick={handleSubmit} autoFocus />
    </>
  );

  const renderSkeleton = () => {
    return (
      <div className="grid">
        {[...Array(5)].map((_, index) => (
          <div key={index} className="col-12 mb-3">
            <div className="flex justify-content-between align-items-center">
              <Skeleton width="60%" height="2rem" />
              <Skeleton width="20%" height="2rem" />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">Manage Users</h5>
      <span className="block mt-2 md:mt-0 p-input-icon-left">
        <i className="pi pi-search" />
        <InputText type="search" onInput={(e) => dt.current?.filter(e.currentTarget.value, 'global', 'contains')} placeholder="Search..." />
      </span>
    </div>
  );

  return (
    <div className="grid">
      <div className="col-12">
        <div className="card">
          <ConfirmDialog />
          {loading ? (
            renderSkeleton()
          ) : (
            <DataTable
              ref={dt}
              value={users}
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 25]}
              responsiveLayout="scroll"
              emptyMessage="No users found."
              header={header}
            >
              <Column field="username" header="Username" sortable filter filterPlaceholder="Search by username" />
              <Column field="email" header="Email" sortable filter filterPlaceholder="Search by email" />
              <Column field="role" header="Role" sortable filter filterElement={(options) => (
                <Dropdown
                  value={options.value}
                  options={roleOptions}
                  onChange={(e) => options.filterCallback(e.value)}
                  placeholder="Select Role"
                  className="p-column-filter"
                />
              )} />
              <Column field="client" header="Client" body={clientBodyTemplate} sortable />
              <Column field="createdAt" header="Created At" body={(rowData) => formatDate(rowData.createdAt)} sortable />
              <Column body={actionBodyTemplate} headerStyle={{ width: '15%' }} bodyStyle={{ textAlign: 'center' }} />
            </DataTable>
          )}
          <div className="flex justify-content-end mt-3">
            <Button label="Add New User" icon="pi pi-plus" className="p-button-success" onClick={() => handleOpenDialog()} />
          </div>

          <Dialog visible={openDialog} style={{ width: '450px' }} header={editingUser ? 'Edit User' : 'Add New User'} modal className="p-fluid" footer={dialogFooter} onHide={handleCloseDialog}>
            <div className="field">
              <label htmlFor="username">Username</label>
              <InputText id="username" name="username" value={formData.username} onChange={handleInputChange} required autoFocus />
            </div>
            <div className="field">
              <label htmlFor="email">Email</label>
              <InputText id="email" name="email" value={formData.email} onChange={handleInputChange} required />
            </div>
            <div className="field">
              <label htmlFor="password">Password {editingUser && '(leave empty to keep current password)'}</label>
              <Password 
                id="password" 
                name="password" 
                value={formData.password} 
                onChange={handleInputChange} 
                required={!editingUser}
                feedback={true}
                promptLabel="Enter password"
                weakLabel="Too short - minimum 6 characters"
                mediumLabel="Medium complexity"
                strongLabel="Strong password"
              />
              {formErrors.password && <small className="p-error">{formErrors.password}</small>}
            </div>
            <div className="field">
              <label htmlFor="role">Role</label>
              <Dropdown id="role" name="role" value={formData.role} options={roleOptions} onChange={handleRoleChange} placeholder="Select a Role" />
            </div>
            {formData.role === UserRole.Customer && (
              <div className="field">
                <label htmlFor="client">Client</label>
                <Dropdown
                  id="client"
                  name="client"
                  value={formData.clientId}
                  options={clientOptions}
                  onChange={handleClientChange}
                  placeholder="Select a Client"
                  required
                />
              </div>
            )}
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
